import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardActions, Stack, Typography, List, ListItem, IconButton, Button, Box, useTheme } from '@mui/material';
import { MoreVert, ArrowBack, ArrowForward } from '@mui/icons-material';
import { Task } from './types';
import { useTranslation } from 'react-i18next';

export function TasksCard({ tasks }: { tasks: Task[] }) {
  const theme = useTheme();
  const { t } = useTranslation('common');

  const pageSize = 3;
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(tasks.length / pageSize));
  const pageItems = tasks.slice(page * pageSize, page * pageSize + pageSize);

  const formatSecondary = useMemo(() => (t: Task) => {
    const parts: string[] = [];
    if (t.location) parts.push(t.location);
    if (t.dueDisplay) parts.push(t.dueDisplay);
    return parts.join(' | ');
  }, []);

  return (
    <Card sx={{ borderRadius: 3, minHeight: '350px', backgroundColor: theme.palette.custom.gray[100], boxShadow: '0 1px 2px rgba(16,24,40,0.04),0 0 0 1px rgba(16,24,40,0.08)' }}>
      <CardContent sx={{ pb: 0, pt: 2.25, px: 2.5 }}>
        <Stack direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 0.5 }}>
          <Typography variant="textLG"
            fontWeight={700}
            sx={{ fontSize: 16 }}>Today&apos;s Tasks</Typography>
          <IconButton
            aria-label='Not implemented yet'
            size="small">
            <MoreVert fontSize="small" />
          </IconButton>
        </Stack>
        {/* List */}
        <List disablePadding>
          {pageItems.map((t, idx) => (
            <ListItem
              key={t.id}
              disableGutters
              divider={idx < pageItems.length - 1}
              sx={{
                alignItems: 'center',
                px: 0,
                py: 1.25,
                margin: '6px 0',
                '&:last-of-type': { pb: 1.25 },
              }}>
              <Box sx={{
                mr: '6px',
                width: 36,
                height: 36,
                borderRadius: 1,
                bgcolor: theme.palette.common.white,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                svg: { fontSize: 20, color: '#1E293B' },
                position: 'relative',
              }}>
                {t.icon}
              </Box>
              <Box sx={{ flexGrow: 1, pr: 1 }}>
                <Typography variant="textSM"
                  component="div"
                  sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3, color: '#111928', mb: 0.25 }}>
                  {t.title}
                </Typography>
                <Typography variant="textXS"
                  component="div"
                  sx={{ fontSize: 12.5, color: '#5A6475' }}>
                  {formatSecondary(t)}
                </Typography>
              </Box>
              <Box sx={{ ml: 1 }}>
                <Button
                  href={t.href}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 13,
                    color: '#111928',
                    minWidth: 0,
                    px: 0.25,
                    padding: 0,
                    border: 'none',
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      textDecoration: 'underline',
                      boxShadow: 'none',
                    },
                    '&:focus': {
                      backgroundColor: 'transparent',
                      boxShadow: 'none',
                    },
                  }}>
                  Review
                </Button>
              </Box>
            </ListItem>
          ))}
        </List>
      </CardContent>
      <CardActions sx={{ px: 1.25, pt: 1, pb: 1.25, margin: '4px 8px', display: 'flex', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <IconButton
            aria-label={t('backPage')}
            size="small"
            disabled={page === 0}
            onClick={() => setPage(p => Math.max(0, p - 1))}
            sx={{ width: 36, height: 36, border: '1px solid #E2E8F0', borderRadius: 1, mr: 1 }}>
            <ArrowBack fontSize="small" />
          </IconButton>
          <Typography variant="textXS"
            sx={{ flexGrow: 1, textAlign: 'center', fontSize: 12.5, color: '#374151', fontWeight: 500 }}>
            Page {page + 1} of {pageCount}
          </Typography>
          <IconButton
            aria-label={t('forwardPage')}
            size="small"
            disabled={page >= pageCount - 1}
            onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
            sx={{ width: 36, height: 36, border: '1px solid #E2E8F0', borderRadius: 1, ml: 1 }}>
            <ArrowForward fontSize="small" />
          </IconButton>
        </Box>
      </CardActions>
    </Card>
  );
}
